import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { formatDateTime } from "../../lib/format";

const empty = { name: "", email: "", password: "", role: "client", status: "active", internal_notes: "" };

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [resetPwd, setResetPwd] = useState("");

    const load = async () => {
        const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
        setUsers(data || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (editing) {
                await api.patch(`/admin/users/${editing.id}`, {
                    name: form.name, email: form.email, role: form.role,
                    status: form.status, internal_notes: form.internal_notes,
                });
                if (resetPwd) {
                    await api.post(`/admin/users/${editing.id}/reset-password`, { new_password: resetPwd });
                }
                toast.success("User updated");
            } else {
                await api.post("/admin/users", form);
                toast.success("User created successfully.");
            }
            setOpen(false); setEditing(null); setForm(empty); setResetPwd("");
            await load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        }
    };

    const openEdit = (u) => {
        setEditing(u);
        setForm({
            name: u.name, email: u.email, password: "",
            role: u.role, status: u.status, internal_notes: u.internal_notes || "",
        });
        setResetPwd("");
        setOpen(true);
    };

    const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };

    const toggleStatus = async (u) => {
        const next = u.status === "active" ? "disabled" : "active";
        try {
            await api.patch(`/admin/users/${u.id}`, { status: next });
            toast.success(next === "active" ? "User activated" : "User deactivated");
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const remove = async (u) => {
        if (u.id === me?.id) { toast.error("You cannot delete your own account."); return; }
        const ok = window.confirm(
            `Permanently delete "${u.email}"?\n\n` +
            `This will erase ALL their data:\n` +
            `· Purchases, licenses, activations\n` +
            `· Download history\n` +
            `· Support tickets\n\nThis action CANNOT be undone.`
        );
        if (!ok) return;
        try {
            const { data } = await api.delete(`/admin/users/${u.id}`);
            const d = data?.deleted || {};
            toast.success(`User deleted · ${d.purchases || 0} purchases, ${d.licenses || 0} licenses`);
            await load();
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-users-page">
                <div>
                    <div className="label-mono mb-3">/ User management</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Users</h1>
                </div>
                <button onClick={openCreate} className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="create-user-btn">
                    + Create user
                </button>
            </div>

            <div className="mb-4">
                <Input placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} className="bg-avx-surface border-avx-line h-11 max-w-md" data-testid="users-search" />
            </div>

            <div className="panel overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-avx-surface/80 border-b border-avx-line">
                        <tr className="label-mono text-left">
                            <th className="px-4 py-3 font-normal">Name</th>
                            <th className="px-4 py-3 font-normal">Email</th>
                            <th className="px-4 py-3 font-normal">Role</th>
                            <th className="px-4 py-3 font-normal">Status</th>
                            <th className="px-4 py-3 font-normal">Created</th>
                            <th className="px-4 py-3 font-normal text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-b border-avx-line/60 hover:bg-avx-surface/40" data-testid={`user-row-${u.id}`}>
                                <td className="px-4 py-3">{u.name}</td>
                                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                                <td className="px-4 py-3 font-mono uppercase text-xs">{u.role}</td>
                                <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                                <td className="px-4 py-3 font-mono text-xs">{formatDateTime(u.created_at)}</td>
                                <td className="px-4 py-3 text-right space-x-2">
                                    <button onClick={() => openEdit(u)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-blue hover:underline" data-testid={`edit-user-${u.id}`}>
                                        Edit
                                    </button>
                                    <button onClick={() => toggleStatus(u)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-muted hover:text-avx-text" data-testid={`toggle-user-${u.id}`}>
                                        {u.status === "active" ? "Deactivate" : "Activate"}
                                    </button>
                                    {u.id !== me?.id && (
                                        <button onClick={() => remove(u)} className="text-xs font-mono uppercase tracking-[0.18em] text-avx-danger hover:underline" data-testid={`delete-user-${u.id}`}>
                                            Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {users.length === 0 && <div className="p-8 text-center text-avx-muted text-sm">No users.</div>}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-avx-surface border-avx-line max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit user" : "Create user"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4" data-testid="user-form">
                        <div>
                            <Label className="label-mono">Name</Label>
                            <Input className="mt-2 bg-avx-bg border-avx-line h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" />
                        </div>
                        <div>
                            <Label className="label-mono">Email</Label>
                            <Input type="email" className="mt-2 bg-avx-bg border-avx-line h-11" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" />
                        </div>
                        {!editing && (
                            <div>
                                <Label className="label-mono">Temporary password</Label>
                                <Input type="text" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="user-password-input" />
                            </div>
                        )}
                        {editing && (
                            <div>
                                <Label className="label-mono">Reset password (optional)</Label>
                                <Input type="text" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="Leave empty to keep unchanged" data-testid="user-reset-pwd" />
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-mono">Role</Label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="client">Client</SelectItem>
                                        <SelectItem value="admin">Admin</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-mono">Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">Activo</SelectItem>
                                        <SelectItem value="disabled">Disabled</SelectItem>
                                        <SelectItem value="pending">Pending</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="label-mono">Internal notes</Label>
                            <Textarea rows={3} className="mt-2 bg-avx-bg border-avx-line" value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} data-testid="user-notes-input" />
                        </div>
                        <button type="submit" className="btn-primary w-full py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="user-submit-btn">
                            {editing ? "Save changes" : "Create user"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
